import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import type { EditorElement as EditorElementType } from '../../types';

interface Props {
  element: EditorElementType;
  selected: boolean;
  onSelect: () => void;
}

/** 元素颜色映射：按叙事角色渲染色调 */
const ROLE_COLORS: Record<string, string> = {
  main_character: '#ff8a5c',
  prop: '#5cc8ff',
  background: '#8a7aff',
  decoration: '#ff5cc8',
};

/**
 * 编辑器中的元素网格体
 * - 未选中：半透明着色 mesh
 * - 选中：线框 + 悬浮标签
 * - 支持拖拽移动（按住 shift + 拖动）
 */
export function EditorElement({ element, selected, onSelect }: Props) {
  const meshRef = useRef<THREE.Mesh>(null);
  const groupRef = useRef<THREE.Group>(null);

  const color = useMemo(
    () => element.color ?? ROLE_COLORS[element.narrative?.role ?? 'prop'] ?? '#888888',
    [element.color, element.narrative?.role]
  );

  // 关键帧动画（简单实现的：position 关键帧线性插值）
  useFrame((state) => {
    const kfs = element.keyframes ?? [];
    if (kfs.length >= 2) {
      const time = state.clock.elapsedTime % (kfs[kfs.length - 1].time + 1);
      let current = kfs[0];
      let next = kfs[kfs.length - 1];
      for (let i = 0; i < kfs.length - 1; i++) {
        if (time >= kfs[i].time && time <= kfs[i + 1].time) {
          current = kfs[i];
          next = kfs[i + 1];
          break;
        }
      }
      const span = Math.max(next.time - current.time, 0.001);
      const t = Math.min(Math.max((time - current.time) / span, 0), 1);
      if (groupRef.current) {
        groupRef.current.position.set(
          THREE.MathUtils.lerp(
            current.position?.x ?? element.position.x,
            next.position?.x ?? element.position.x, t
          ),
          THREE.MathUtils.lerp(
            current.position?.y ?? element.position.y,
            next.position?.y ?? element.position.y, t
          ),
          THREE.MathUtils.lerp(
            current.position?.z ?? element.position.z,
            next.position?.z ?? element.position.z, t
          ),
        );
      }
    }
  });

  const handlePointerDown = (e: any) => {
    e.stopPropagation();
    onSelect();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const handleClick = (e: any) => {
    e.stopPropagation();
    onSelect();
  };

  return (
    <group
      ref={groupRef}
      position={[element.position.x, element.position.y, element.position.z]}
      rotation={[element.rotation.x, element.rotation.y, element.rotation.z]}
      scale={[element.scale.x, element.scale.y, element.scale.z]}
    >
      <mesh
        ref={meshRef}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        visible={element.visible}
      >
        <boxGeometry args={[1, 1, 0.15]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={selected ? 0.85 : element.opacity * 0.6}
          roughness={element.narrative?.texture === 'soft' ? 0.9 : 0.4}
          metalness={element.narrative?.texture === 'shiny' ? 0.7 : 0}
        />
        {selected && (
          <mesh>
            <boxGeometry args={[1.06, 1.06, 0.2]} />
            <meshBasicMaterial wireframe color="#ffffff" transparent opacity={0.9} />
          </mesh>
        )}
      </mesh>

      {selected && (
        <Html position={[0, 0.8, 0]} center zIndexRange={[10, 0]}>
          <div
            style={{
              background: 'rgba(20,24,48,0.92)',
              color: '#fff',
              fontSize: 12,
              padding: '3px 10px',
              borderRadius: 10,
              border: '1px solid #5cc8ff',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {element.name} · {element.narrative?.role ?? element.category}
          </div>
        </Html>
      )}
    </group>
  );
}